import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Space, Tag, Typography, Button, Input, Divider, Skeleton, Empty,
    Flex, Avatar, Popconfirm, message, Pagination, Grid, Tooltip, Select, theme
} from 'antd';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    SendOutlined,
    UserOutlined,
    RobotOutlined,
    ClockCircleOutlined,
    CommentOutlined,
    TagOutlined,
    FlagOutlined,
    AppstoreOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    SyncOutlined,
    LockOutlined,
    UnlockOutlined,
    MessageOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { resolveBackendMessage } from '../../../Utils/apiMessages';
import {
    getTicket,
    updateTicket,
    deleteTicket,
    getTicketComments,
    createTicketComment,
    deleteTicketComment,
} from '../TicketServices';
import { getMessages } from '../../Messages/MessagesServices';
import MessageBubble from '../../Messages/Messages/MessageContent/MessageList/MessageBubble/MessageBubble';
import { formatFullTime } from '../../Messages/utils/messageRenderUtils';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import ContactsServices from '../../Contacts/ContactsServices';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const STATUS_COLORS = {
    open: 'blue',
    in_progress: 'orange',
    resolved: 'green',
    closed: 'default',
};

const STATUS_DOT_COLORS = {
    open: '#1677ff',
    in_progress: '#fa8c16',
    resolved: '#52c41a',
    closed: '#8c8c8c',
};

const PRIORITY_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

const CATEGORY_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};



/**
 * A single metadata row for the sidebar.
 */
const MetaRow = ({ icon, label, children }) => (
    <Flex align="flex-start" gap={10} style={{ fontSize: 13, lineHeight: '22px' }}>
        <span style={{ opacity: 0.45, width: 16, flexShrink: 0, textAlign: 'center', paddingTop: 2 }}>{icon}</span>
        <Text type="secondary" style={{ minWidth: 80, flexShrink: 0, fontSize: 12 }}>{label}</Text>
        <div style={{ flex: 1 }}>{children}</div>
    </Flex>
);

const TicketDetail = ({ ticketId, onClose }) => {
    const id = ticketId;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const { setLoading } = useGlobal();
    const { handleError } = useErrorHandler();
    const screens = useBreakpoint();
    const { token } = theme.useToken();

    const [ticket, setTicket] = useState(null);
    const [loading, setLocalLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [commentsMeta, setCommentsMeta] = useState(null);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentPage, setCommentPage] = useState(1);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    const [statusChanging, setStatusChanging] = useState(false);

    const [contextMessages, setContextMessages] = useState([]);
    const [loadingContext, setLoadingContext] = useState(false);
    const messageRefs = useRef({});
    const messageListRef = useRef(null);

    useEffect(() => {
        const fetchContext = async () => {
            if (!ticket?.source_message_id || !ticket?.contact_id) return;
            setLoadingContext(true);
            try {
                const conversationResponse = await ContactsServices.getContactConversation(ticket.contact_id);
                const conversationId = conversationResponse?.data?.id;

                if (!conversationId) return;

                const messageId = ticket.source_message_id;
                const contextSize = 10;

                const [olderResponse, newerResponse] = await Promise.all([
                    getMessages({ conversation_id: conversationId, after: messageId, size: contextSize }),
                    getMessages({ conversation_id: conversationId, until: messageId, size: contextSize })
                ]);

                const newerMessages = newerResponse?.data || [];
                const olderMessages = olderResponse?.data || [];
                
                const combined = [...newerMessages, ...olderMessages];
                const unique = Array.from(new Map(combined.map(m => [m.id, m])).values());
                unique.sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));

                setContextMessages(unique);
            } catch (error) {
                console.error('Failed to fetch message context:', error);
            } finally {
                setLoadingContext(false);
            }
        };

        if (ticket?.source_message_id) {
            fetchContext();
        }
    }, [ticket?.source_message_id, ticket?.contact_id]);

    useEffect(() => {
        if (contextMessages.length > 0 && ticket?.source_message_id && messageListRef.current) {
            const targetRef = messageRefs.current[ticket.source_message_id]?.current;
            const container = messageListRef.current;
            if (targetRef && container) {
                setTimeout(() => {
                    const topOffset = targetRef.offsetTop - container.offsetTop;
                    const centerScroll = topOffset - (container.clientHeight / 2) + (targetRef.clientHeight / 2);
                    container.scrollTo({ top: centerScroll, behavior: 'smooth' });
                }, 150);
            }
        }
    }, [contextMessages, ticket?.source_message_id]);

    const fetchTicket = useCallback(async () => {
        try {
            setLocalLoading(true);
            const { data } = await getTicket(id);
            setTicket(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLocalLoading(false);
        }
    }, [id, handleError]);

    const fetchComments = useCallback(async (page = 1) => {
        try {
            setCommentsLoading(true);
            const { data, metadata } = await getTicketComments({
                ticketId: id,
                page,
                size: 25,
                sort_by: 'created_at',
                sort_direction: 'desc',
            });
            setComments(data || []);
            setCommentsMeta(metadata);
        } catch (error) {
            handleError(error);
        } finally {
            setCommentsLoading(false);
        }
    }, [id, handleError]);

    useEffect(() => {
        fetchTicket();
        fetchComments(1);
    }, [fetchTicket, fetchComments]);

    const handleStatusChange = async (newStatus) => {
        try {
            setStatusChanging(true);
            setLoading(true);
            const payload = await updateTicket(id, { status: newStatus });
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            setTicket(prev => ({ ...prev, status: newStatus }));
        } catch (error) {
            handleError(error);
        } finally {
            setStatusChanging(false);
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const payload = await deleteTicket(id);
            const text = resolveBackendMessage(t, payload);
            if (text) message.success(text);
            onClose();
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            setSubmittingComment(true);
            await createTicketComment(id, { content: newComment.trim() });
            setNewComment('');
            fetchComments(commentPage);
        } catch (error) {
            handleError(error);
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await deleteTicketComment(commentId);
            fetchComments(commentPage);
        } catch (error) {
            handleError(error);
        }
    };

    const shortId = (ticketId) => {
        if (!ticketId) return '';
        return `#${String(ticketId).slice(0, 5).toUpperCase()}`;
    };

    // ─── Loading skeleton ───
    if (loading) {
        return (
            <ResponsiveModal
                open={!!ticketId}
                onCancel={onClose}
                desktopWidth={1000}
                title={t('loading')}
                footer={null}
            >
                <div style={{ padding: '16px' }}>
                    <Card>
                        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: ['40%'] }} />
                    </Card>
                    <Flex gap={16} style={{ marginTop: 16 }} wrap="wrap">
                        <Card style={{ flex: 2, minWidth: 300 }}>
                            <Skeleton active paragraph={{ rows: 5 }} />
                        </Card>
                        <Card style={{ flex: 1, minWidth: 240 }}>
                            <Skeleton active paragraph={{ rows: 4 }} />
                        </Card>
                    </Flex>
                </div>
            </ResponsiveModal>
        );
    }

    // ─── Not found ───
    if (!ticket) {
        return (
            <ResponsiveModal
                 open={!!ticketId}
                 onCancel={onClose}
                 desktopWidth={600}
                 title={t('ticket_not_found')}
                 footer={null}
            >
                <Empty description={t('ticket_not_found')} />
                <Flex justify="center" style={{ marginTop: 16 }}>
                    <Button onClick={onClose}>{t('close')}</Button>
                </Flex>
            </ResponsiveModal>
        );
    }

    const isCompact = !screens.md;

    return (
        <ResponsiveModal
            open={!!ticketId}
            onCancel={onClose}
            desktopWidth={1000}
            title={ticket ? ticket.subject : t('ticket_details')}
            footer={null}
            destroyOnClose
            styles={{ body: { padding: isCompact ? '16px 8px' : '24px' } }}
        >
            <Flex vertical gap={16}>
                {/* ─── Top Bar ─── */}
                <Flex
                    align="center"
                    justify="flex-end"
                    wrap="wrap"
                    gap={8}
                    style={{ marginBottom: 16 }}
                >

                <Space>
                    <Select
                        value={ticket.status}
                        onChange={handleStatusChange}
                        loading={statusChanging}
                        style={{ width: 140 }}
                        options={['open', 'in_progress', 'resolved', 'closed'].map(s => ({
                            label: (
                                <Space size={6}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: STATUS_DOT_COLORS[s],
                                    }} />
                                    {t(`ticket_status_${s}`)}
                                </Space>
                            ),
                            value: s,
                        }))}
                    />
                    <Popconfirm
                        title={t('delete_ticket_confirmation')}
                        onConfirm={handleDelete}
                        okText={t('delete')}
                        cancelText={t('cancel')}
                        okType="danger"
                    >
                        <Button danger icon={<DeleteOutlined />} size="middle" />
                    </Popconfirm>
                </Space>
            </Flex>

            {/* ─── Main Content ─── */}
            <Flex gap={16} wrap="wrap" align="flex-start">

                {/* LEFT: Description + Comments */}
                <Flex vertical gap={16} style={{ flex: 2, minWidth: 0 }}>

                    {/* Subject & Description */}
                    <Card
                        styles={{ body: { padding: '24px 24px 20px' } }}
                        style={{ borderRadius: 10 }}
                    >
                        {/* Subject — truncated to 2 lines */}
                        <Title
                            level={5}
                            style={{
                                margin: '0 0 12px 0',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                            }}
                        >
                            {ticket.subject || t('no_subject')}
                        </Title>

                        {/* Tags row — distinct visual band */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                            padding: '8px 12px',
                            borderRadius: 8,
                            background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            marginBottom: 16,
                        }}>
                            <Tag
                                color={STATUS_COLORS[ticket.status]}
                                style={{ borderRadius: 4, fontWeight: 600, margin: 0 }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: STATUS_DOT_COLORS[ticket.status],
                                        marginInlineEnd: 5,
                                        verticalAlign: 'middle',
                                    }}
                                />
                                {t(`ticket_status_${ticket.status}`)}
                            </Tag>
                            {ticket.category && (
                                <Tag color={CATEGORY_COLORS[ticket.category]} style={{ borderRadius: 4, margin: 0 }}>
                                    {t(`ticket_category_${ticket.category}`)}
                                </Tag>
                            )}
                            {ticket.priority && (
                                <Tag color={PRIORITY_COLORS[ticket.priority]} style={{ borderRadius: 4, margin: 0 }}>
                                    {t(`ticket_priority_${ticket.priority}`)}
                                </Tag>
                            )}
                            {ticket.opened_by_type && (
                                <Tag
                                    icon={ticket.opened_by_type === 'agent' ? <RobotOutlined /> : <UserOutlined />}
                                    style={{ borderRadius: 4, margin: 0 }}
                                >
                                    {t(`opened_by_${ticket.opened_by_type}`)}
                                </Tag>
                            )}
                        </div>

                        {/* Description — only show if different from subject */}
                        {ticket.description && ticket.description !== ticket.subject ? (
                            <>
                                <Text type="secondary" strong style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {t('ticket_description')}
                                </Text>
                                <Paragraph style={{
                                    whiteSpace: 'pre-wrap',
                                    marginBottom: 0,
                                    marginTop: 6,
                                    fontSize: 14,
                                    lineHeight: 1.7,
                                }}>
                                    {ticket.description}
                                </Paragraph>
                            </>
                        ) : !ticket.description ? (
                            <Text type="secondary" italic>{t('no_description')}</Text>
                        ) : null}

                        {/* Admin/Agent responses */}
                        {(ticket.admin_response || ticket.agent_response) && (
                            <>
                                <Divider style={{ margin: '16px 0 12px' }} />
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {ticket.admin_response && (
                                        <div style={{
                                            padding: '10px 14px',
                                            borderRadius: 8,
                                            background: isDarkMode ? 'rgba(22,119,255,0.08)' : 'rgba(22,119,255,0.04)',
                                            borderInlineStart: '3px solid #1677ff',
                                        }}>
                                            <Text strong style={{ fontSize: 12, color: '#1677ff' }}>
                                                {t('admin_response')}
                                            </Text>
                                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0, marginTop: 4, fontSize: 13 }}>
                                                {typeof ticket.admin_response === 'object' ? ticket.admin_response.content : ticket.admin_response}
                                            </Paragraph>
                                        </div>
                                    )}
                                    {ticket.agent_response && (
                                        <div style={{
                                            padding: '10px 14px',
                                            borderRadius: 8,
                                            background: isDarkMode ? 'rgba(82,196,26,0.08)' : 'rgba(82,196,26,0.04)',
                                            borderInlineStart: '3px solid #52c41a',
                                        }}>
                                            <Text strong style={{ fontSize: 12, color: '#52c41a' }}>
                                                {t('agent_response')}
                                            </Text>
                                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0, marginTop: 4, fontSize: 13 }}>
                                                {typeof ticket.agent_response === 'object' ? ticket.agent_response.content : ticket.agent_response}
                                            </Paragraph>
                                        </div>
                                    )}
                                </Space>
                            </>
                        )}
                    </Card>

                    {/* Source Message Context */}
                    {ticket.source_message_id && (
                        <Card
                            styles={{ body: { padding: '14px 16px' } }}
                            style={{ borderRadius: 10 }}
                            title={
                                <Flex align="center" gap={6}>
                                    <MessageOutlined style={{ fontSize: 12 }} />
                                    <Text style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        {t('message_context')}
                                    </Text>
                                </Flex>
                            }
                        >
                            {loadingContext ? (
                                <Skeleton active paragraph={{ rows: 4 }} />
                            ) : (
                                <div ref={messageListRef} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
                                    {contextMessages.map((msg) => {
                                        const isTarget = msg.id === ticket.source_message_id;
                                        return (
                                            <div 
                                                key={msg.id} 
                                                style={{ 
                                                    opacity: isTarget ? 1 : 0.6,
                                                    transform: isTarget ? 'scale(1)' : 'scale(0.95)',
                                                    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                                    padding: isTarget ? '4px' : '0',
                                                    margin: isTarget ? '4px 0' : '0',
                                                    ...(isTarget ? {
                                                        outline: `2px solid ${token.colorPrimary}`,
                                                        outlineOffset: '2px',
                                                        borderRadius: '12px',
                                                        background: isDarkMode ? `${token.colorPrimary}15` : `${token.colorPrimary}0A`,
                                                        boxShadow: `0 8px 24px -6px ${token.colorPrimary}40`,
                                                    } : {})
                                                }}
                                            >
                                                <MessageBubble
                                                    message={msg}
                                                    screens={screens}
                                                    messageRefs={messageRefs}
                                                    isViewOnly={true}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <Button
                                type="link"
                                size="small"
                                icon={<LinkOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                    navigate(`/messages/${ticket.source_message_id}`);
                                }}
                                style={{ paddingInlineStart: 0, marginTop: 12, fontSize: 12 }}
                            >
                                {t('view_full_conversation')}
                            </Button>
                        </Card>
                    )}

                    {/* ─── Comments ─── */}
                    <Card
                        styles={{ body: { padding: '16px 20px' } }}
                        style={{ borderRadius: 10 }}
                        title={
                            <Flex align="center" gap={8}>
                                <CommentOutlined />
                                <span>{t('comments')}</span>
                                {commentsMeta?.total > 0 && (
                                    <Tag style={{ borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                        {commentsMeta.total}
                                    </Tag>
                                )}
                            </Flex>
                        }
                    >
                        {/* Comment input */}
                        <div style={{
                            padding: 12,
                            borderRadius: 10,
                            background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            marginBottom: 20,
                        }}>
                            <TextArea
                                value={newComment}
                                onChange={e => setNewComment(e.target.value)}
                                placeholder={t('add_comment_placeholder')}
                                rows={2}
                                maxLength={2000}
                                variant="borderless"
                                style={{ fontSize: 13, resize: 'none' }}
                                onPressEnter={e => {
                                    if (!e.shiftKey) {
                                        e.preventDefault();
                                        handleAddComment();
                                    }
                                }}
                            />
                            <Flex justify="flex-end" style={{ marginTop: 8 }}>
                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    size="middle"
                                    loading={submittingComment}
                                    onClick={handleAddComment}
                                    disabled={!newComment.trim()}
                                >
                                    {t('send')}
                                </Button>
                            </Flex>
                        </div>

                        {/* Comment thread */}
                        {commentsLoading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : comments.length === 0 ? (
                            <Empty
                                description={t('no_comments')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                style={{ padding: '20px 0' }}
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {comments.map((comment) => (
                                    <div
                                        key={comment.id}
                                        style={{
                                            padding: '12px 14px',
                                            borderRadius: 8,
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Flex gap={10} align="flex-start">
                                            <Avatar
                                                size={30}
                                                icon={<UserOutlined />}
                                                style={{
                                                    flexShrink: 0,
                                                    background: isDarkMode ? '#264f78' : '#e6f4ff',
                                                    color: isDarkMode ? '#69b1ff' : '#1677ff',
                                                }}
                                            />
                                            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                                <Flex align="center" gap={8} style={{ marginBottom: 2 }}>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                        {formatFullTime(comment.created_at) || comment.created_at}
                                                    </Text>
                                                </Flex>
                                                <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                    {comment.content}
                                                </Text>
                                            </Flex>
                                            <Popconfirm
                                                title={t('delete_comment_confirmation')}
                                                onConfirm={() => handleDeleteComment(comment.id)}
                                                okText={t('delete')}
                                                cancelText={t('cancel')}
                                                okType="danger"
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    style={{ opacity: 0.4, flexShrink: 0 }}
                                                />
                                            </Popconfirm>
                                        </Flex>
                                    </div>
                                ))}
                            </div>
                        )}

                        {commentsMeta && commentsMeta.total > commentsMeta.page_size && (
                            <Flex justify="center" style={{ marginTop: 16 }}>
                                <Pagination
                                    current={commentsMeta.page}
                                    pageSize={commentsMeta.page_size}
                                    total={commentsMeta.total}
                                    onChange={(page) => {
                                        setCommentPage(page);
                                        fetchComments(page);
                                    }}
                                    size="small"
                                />
                            </Flex>
                        )}
                    </Card>
                </Flex>

                {/* ─── RIGHT SIDEBAR ─── */}
                <Flex
                    vertical
                    gap={16}
                    style={{
                        flex: isCompact ? '100%' : undefined,
                        width: isCompact ? '100%' : 280,
                        minWidth: isCompact ? undefined : 260,
                        position: isCompact ? 'static' : 'sticky',
                        top: 16,
                        alignSelf: 'flex-start',
                    }}
                >
                    {/* Contact Card */}
                    {ticket.contact && (
                        <Card
                            styles={{ body: { padding: '16px 20px' } }}
                            style={{ borderRadius: 10 }}
                            title={
                                <Text style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    {t('contact')}
                                </Text>
                            }
                        >
                            <Flex gap={10} align="center">
                                <Avatar
                                    size={36}
                                    src={ticket.contact.picture_url}
                                    icon={<UserOutlined />}
                                    style={{
                                        background: isDarkMode ? '#264f78' : '#e6f4ff',
                                        color: isDarkMode ? '#69b1ff' : '#1677ff',
                                        flexShrink: 0,
                                    }}
                                />
                                <Flex vertical style={{ minWidth: 0 }}>
                                    <Text strong style={{ fontSize: 13 }}>
                                        {ticket.contact.full_name}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        {ticket.contact.contact_type}
                                    </Text>
                                    {ticket.contact.email && (
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {ticket.contact.email}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        </Card>
                    )}


                    {/* Details Card */}
                    <Card
                        styles={{ body: { padding: '16px 20px' } }}
                        style={{ borderRadius: 10 }}
                        title={
                            <Text style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {t('ticket_details')}
                            </Text>
                        }
                    >
                        <Flex vertical gap={14}>
                            <MetaRow icon={<AppstoreOutlined />} label={t('ticket_id')}>
                                <Text strong style={{ fontFamily: 'monospace' }}>{shortId(ticket.id)}</Text>
                            </MetaRow>

                            <MetaRow icon={<FlagOutlined />} label={t('ticket_status')}>
                                <Tag
                                    color={STATUS_COLORS[ticket.status]}
                                    style={{ borderRadius: 4, margin: 0 }}
                                >
                                    {t(`ticket_status_${ticket.status}`)}
                                </Tag>
                            </MetaRow>

                            <MetaRow icon={<TagOutlined />} label={t('ticket_priority')}>
                                <Tag
                                    color={PRIORITY_COLORS[ticket.priority]}
                                    style={{ borderRadius: 4, margin: 0 }}
                                >
                                    {t(`ticket_priority_${ticket.priority}`)}
                                </Tag>
                            </MetaRow>

                            {ticket.category && (
                                <MetaRow icon={<AppstoreOutlined />} label={t('ticket_category')}>
                                    <Tag
                                        color={CATEGORY_COLORS[ticket.category]}
                                        style={{ borderRadius: 4, margin: 0 }}
                                    >
                                        {t(`ticket_category_${ticket.category}`)}
                                    </Tag>
                                </MetaRow>
                            )}

                            <MetaRow icon={ticket.opened_by_type === 'agent' ? <RobotOutlined /> : <UserOutlined />} label={t('opened_by_type')}>
                                <Text style={{ fontSize: 13 }}>
                                    {t(`opened_by_${ticket.opened_by_type}`) || ticket.opened_by_type}
                                </Text>
                            </MetaRow>

                            <Divider style={{ margin: '4px 0' }} />

                            <MetaRow icon={<CalendarOutlined />} label={t('created_at')}>
                                <Text style={{ fontSize: 12 }}>
                                    {formatFullTime(ticket.created_at) || ticket.created_at}
                                </Text>
                            </MetaRow>

                            {ticket.resolved_at && (
                                <MetaRow icon={<CheckCircleOutlined />} label={t('resolved_at')}>
                                    <Text style={{ fontSize: 12 }}>
                                        {formatFullTime(ticket.resolved_at) || ticket.resolved_at}
                                    </Text>
                                </MetaRow>
                            )}

                            {ticket.closed_at && (
                                <MetaRow icon={<LockOutlined />} label={t('closed_at')}>
                                    <Text style={{ fontSize: 12 }}>
                                        {formatFullTime(ticket.closed_at) || ticket.closed_at}
                                    </Text>
                                </MetaRow>
                            )}

                            {ticket.resolved_by && (
                                <MetaRow icon={<UserOutlined />} label={t('resolved_by')}>
                                    <Text style={{ fontSize: 12 }}>
                                        {ticket.resolved_by.full_name}
                                    </Text>
                                </MetaRow>
                            )}
                        </Flex>
                    </Card>
                </Flex>
            </Flex>
            </Flex>
        </ResponsiveModal>
    );
};

export default TicketDetail;
